import { BaseComponent } from '../../base-component';
import { VideoPlayer, VideoPlayerEvent } from './video-player';
import { CliplisterStage } from './cliplister-settings';

export type VideoTrackingObject = {
    product_Video?: string;
    product_VideoProgress?: string;
    product_VideoQuality?: string;
    product_VideoError?: string;
    product_VideoCount?: number;
    product_VideoLength?: number;
    product_VideoPosition?: number;
};

type VideoInitEvent = {
    parentId?: string;
    assetKey?: string;
    backgroundColor?: string;
    stage?: CliplisterStage;
    autoplay?: boolean;
    withNextButton?: boolean;
    onAspectRatioChange?: (aspectRatio: string) => void;
};

type VideoConfig = {
    parentId: string;
    assetKey: string;
    backgroundColor: string;
    stage: CliplisterStage;
    autoplay: boolean;
    withNextButton: boolean;
    onAspectRatioChange?: (aspectRatio: string) => void;
};

type VideoPlayEvent = {
    parentId: string;
};

type VideoPauseEvent = {
    parentId: string;
};

type PaliLayer = { open: () => void; setContent: (content: string) => void };

export class VideoComponent extends BaseComponent {
    public rootSelector = '';

    private static readonly playerParentIdForLayer = 'videoPlayerArea';

    public assetKey: string | undefined;
    public paliLayer?: PaliLayer;
    private instances = new Map<string, VideoPlayer>();

    public async init(): Promise<void> {
        this.registerInitEvent();
        this.registerPlayEvent();
        this.registerPauseEvent();
    }

    public async onHtmlLoaded(): Promise<void> {
        return;
    }

    private createAndOpenLayer(): void {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.paliLayer = new o_global.pali.layerBuilder({
            modal: true,
            closeCallback: () => {
                this.destroyPlayer(VideoComponent.playerParentIdForLayer);
                this.trackEvent({ product_Video: 'close' });
            },
            createTrackingContext: false,
            heightLXL: '720px',
            layerClass: 'p_layer',
            content: this.getLayerContent(),
        });

        this.paliLayer?.open();
    }

    private async initPlayer(config: VideoConfig): Promise<void> {
        const videoPlayer = new VideoPlayer({
            ...config,
            onProgress10: event => this.trackPlayerEvent(event, 'progress_0.1'),
            onProgress50: event => this.trackPlayerEvent(event, 'progress_0.5'),
            onProgress90: event => this.trackPlayerEvent(event, 'progress_0.9'),
            onQualityChange: event => this.trackPlayerEvent(event, 'changeQuality'),
            onInitialVideoStart: event => this.trackPlayerEvent(event, 'start'),
            onFullscreenChange: event => this.trackPlayerEvent(event, event.isFullscreen ? 'openFullscreen' : 'closeFullscreen'),
            onChangeVideo: event => this.trackPlayerEvent(event, 'changeVideo'),
        });
        await videoPlayer.init();
        this.instances.set(config.parentId, videoPlayer);
    }

    private trackPlayerEvent(event: VideoPlayerEvent, trackingEventName: string): void {
        this.trackEvent({
            product_Video: trackingEventName,
            product_VideoCount: event.videoCount,
            product_VideoLength: event.videoLength,
            product_VideoPosition: event.videoPosition,
            product_VideoQuality: event.videoQuality,
        });
    }

    private destroyPlayer(targetElementId: string): void {
        this.instances.get(targetElementId)?.destroy();
        this.instances.delete(targetElementId);
    }

    private async handleInitEvent(event: VideoInitEvent): Promise<void> {
        if (event.assetKey) {
            const config: VideoConfig = {
                parentId: event.parentId ?? VideoComponent.playerParentIdForLayer,
                assetKey: event.assetKey,
                backgroundColor: event.backgroundColor ?? '#000000',
                stage: event.stage ?? { aspectRatio: 'asset', width: '100%' },
                autoplay: event.autoplay ?? true,
                withNextButton: event.withNextButton ?? true,
                onAspectRatioChange: event.onAspectRatioChange,
            };
            if (config.parentId === VideoComponent.playerParentIdForLayer) {
                this.createAndOpenLayer();
                this.trackEvent({ product_Video: 'open' });
            }
            try {
                await this.initPlayer(config);
            } catch (error: unknown) {
                this.showErrorMessageInLayer();
                this.trackEvent({ product_Video: (error as Error)?.message });
            }
        }
    }

    private async handlePlayEvent(event: VideoPlayEvent): Promise<void> {
        this.instances.get(event.parentId)?.play();
    }

    private async handlePauseEvent(event: VideoPauseEvent): Promise<void> {
        this.instances.get(event.parentId)?.pause();
    }

    private registerInitEvent(): void {
        o_global.eventQBus.on('ft5.pdp-video.init', async (event: VideoInitEvent) => this.handleInitEvent(event || {}));
    }

    private registerPlayEvent(): void {
        o_global.eventQBus.on('ft5.pdp-video.play', async (event: VideoPlayEvent) => this.handlePlayEvent(event || {}));
    }

    private registerPauseEvent(): void {
        o_global.eventQBus.on('ft5.pdp-video.pause', async (event: VideoPauseEvent) => this.handlePauseEvent(event || {}));
    }

    private showErrorMessageInLayer(): void {
        this.paliLayer?.setContent(
            this.getLayerContent(
                '<div class="p_message p_message--error">Es ist ein technischer Fehler aufgetreten. Bitte rufe das Video erneut auf.</div>'
            )
        );
    }

    private getLayerContent(bodyHtml = ''): string {
        return `<h2 class="p_layer__headline">Videos zu diesem Artikel</h2>
        <div class="p_layer__body" id="${VideoComponent.playerParentIdForLayer}">${bodyHtml}</div>`;
    }
}
